import type { AxiosRequestConfig } from 'axios';
import http, { HttpSuccessOrErrorType } from '../http'

/** 可选 AbortSignal，用于取消重复请求 */
export type GroupRevenueAnalysisRequestConfig = Pick<AxiosRequestConfig, 'signal'>;

export default {

    // 集团营业总额 
    async get_restaurant_group_revenue_dashboard(
        stringId: string,
        params: GetRestaurantGroupRevenueDashboardParams,
        requestConfig?: GroupRevenueAnalysisRequestConfig
    ): Promise<HttpSuccessOrErrorType<any>> {
        return await http.get(`/restaurant/${stringId}/group-revenue-dashboard`, {
            params,
            ...requestConfig,
        })
    },

    // 营收数据分析曲线
    async get_group_revenue_trend(
        stringId: string,
        params: GetRestaurantGroupRevenueDashboardParams,
        requestConfig?: GroupRevenueAnalysisRequestConfig
    ): Promise<HttpSuccessOrErrorType<StatisticsResponse>> {
        return await http.get(`/group/${stringId}/revenue-trend`, {
            params,
            ...requestConfig,
        })
    },

    // 集团菜品分析（旧版 Tab，保留兼容）
    async get_group_menu_sales_analysis(stringId: string, params: GetGroupMenuSalesAnalysisParams): Promise<HttpSuccessOrErrorType<MenuSalesAnalysisResponse>> {
        return await http.get(`/group/${stringId}/menu-sales-analysis`, {
            params
        })
    },

    /** 集团内门店菜单合集：下拉筛选用 */
    async get_group_menu_catalog_options(
        stringId: string,
        params?: GetGroupMenuCatalogOptionsParams,
        requestConfig?: GroupRevenueAnalysisRequestConfig
    ): Promise<HttpSuccessOrErrorType<GroupMenuCatalogOptionsResponse>> {
        return await http.get(`/group/${stringId}/menu-catalog-options`, {
            params,
            ...requestConfig,
        })
    },

    /** 集团菜单销售分析（按菜单名维度、多门店趋势 + 排名） */
    async get_group_menu_sales_by_menu_name(
        stringId: string,
        params: GetGroupMenuSalesByMenuNameParams,
        requestConfig?: GroupRevenueAnalysisRequestConfig
    ): Promise<HttpSuccessOrErrorType<MenuSalesByMenuNameResponse>> {
        return await http.get(`/group/${stringId}/menu-sales-by-menu-name`, {
            params,
            ...requestConfig,
        })
    },

}

type GetGroupMenuSalesAnalysisParams = GetRestaurantGroupRevenueDashboardParams & {
    type: MenuSalesAnalysisType,
    // 默认 ALL 单个 rest_001 多个 rest_001,rest_002,rest_003
    restaurant_ids?: string
}
/**
 * type enum<string> 
 * 枚举值: menu: 菜单销售分析, category: 小类分析, item: 菜品分析
 */
export enum MenuSalesAnalysisType {
    Menu = 'menu',
    Category = 'category',
    Item = 'item'
}


// 集团营业总额 入参
type GetRestaurantGroupRevenueDashboardParams = {
    end_time?: string;
    /**
     * 模式  'today' | 'month' | 'year' | 'custom',    custom自定义
     */
    mode: Mode;
    start_time?: string;
    /**
     * 不传默认查询总额（'2' 外卖  '1' 堂食）
     */
    type?: string;
}

/**
 * 模式  'today' | 'month' | 'week' | 'custom',    custom自定义
 */
export enum Mode {
    Month = 'month',
    Today = 'today',
    // Year = 'year',
    Week = 'week',
    Custom = 'custom'
}

/** 集团菜单销售分析：折线图 Y 轴展示维度（前端切换；接口在同一点上同时返回 amount 与 quantity） */
export enum MenuSalesMetric {
    Amount = 'amount',
    Quantity = 'quantity',
}

/** GET menu-catalog-options 可选参数 */
type GetGroupMenuCatalogOptionsParams = {
    /** 已选门店 id，逗号分隔，用于缩小可选菜单/类目范围 */
    restaurant_ids?: string;
};

/** GET menu-sales-by-menu-name 查询参数（menu_name 必传） */
type GetGroupMenuSalesByMenuNameParams = GetRestaurantGroupRevenueDashboardParams & {
    /** 必传：集团菜单名称 */
    menu_name: string;
    restaurant_ids?: string;
    category_name?: string;
    dish_name?: string;
};

/**
 * 仪表盘/图表统计数据返回结构
 */
export interface StatisticsResponse {
    /** 字符串标识符，例如项目或业务线的 ID */
    string_id: string;

    /** 模式，例如 'custom' (自定义) */
    mode: Mode;

    /** 时间聚合粒度，如 'day' (天), 'hour' (小时) */
    granularity: Granularity;

    /** 当前主统计指标名称，如 'revenue' (营收) */
    metric: string;

    /** 查询的时间范围 */
    time_range: TimeRange;

    /** 数据序列列表，包含每日的具体数值 */
    series: DataPoint[];
}

enum Granularity {
    Day = 'day',
    Week = 'week',
    Month = 'month',
    Hour = 'hour'
}

/**
 * 时间范围对象
 */
interface TimeRange {
    /** 开始时间，格式：YYYY-MM-DD HH:mm:ss */
    start: string;
    /** 结束时间，格式：YYYY-MM-DD HH:mm:ss */
    end: string;
}

/**
 * 单个时间节点的数据点
 */
interface DataPoint {
    /** X轴坐标，通常为日期字符串 (YYYY-MM-DD) */
    x: string;

    /** 营收总额 */
    revenue: number;

    /** 订单总数 */
    orders: number;

    /** 平均客单价 (revenue / orders) */
    avg_order_amount: number;
}


/**
 * 营收统计报表主接口
 */
export interface RevenueReport {
    /** 业务标识 ID */
    string_id: string;
    /** 统计模式，例如：'month' (按月), 'custom' (自定义) */
    mode: Mode;
    /** 时间聚合粒度，如 'day' (天), 'hour' (小时) */
    granularity: Granularity;

    /** 当前查询的时间范围 */
    time_range: DateTimeRange;
    /** 环比（上一个周期）的时间范围 */
    last_period_range: DateTimeRange;

    /** 餐厅总数 */
    restaurant_count: number;
    /** 数据抓取成功的餐厅数量 */
    success_count: number;
    /** 数据抓取失败的餐厅数量 */
    failed_count: number;

    /** 本期总营收 */
    total_revenue: number;
    /** 上期总营收 (用于计算环比) */
    total_revenue_last: number;
    /** 营收环比增长百分比，带符号字符串，如 "-92.95%" */
    mom_percent: string;

    /** 本期总订单量 */
    total_orders: number;
    /** 上期总订单量 */
    total_orders_last: number;
    /** 订单量环比增长百分比，带符号字符串 */
    orders_mom_percent: string;

    /** 全局平均客单价 (total_revenue / total_orders) */
    avg_order_amount: number;
    /** 上期平均客单价 */
    avg_order_amount_last: number;
    /** 平均客单价环比增长百分比，带符号字符串 */
    avg_order_amount_mom_percent: string;

    /** 全局支付方式拆解统计 */
    payment_breakdown: PaymentDetail[];

    /** 按餐厅划分的明细数据 */
    breakdown: RestaurantBreakdownItem[];
}

/**
 * 时间范围定义
 */
interface DateTimeRange {
    /** 开始时间，格式: "YYYY-MM-DD HH:mm:ss" */
    start: string;
    /** 结束时间，格式: "YYYY-MM-DD HH:mm:ss" */
    end: string;
}

/**
 * 支付方式明细
 */
interface PaymentDetail {
    /** 支付渠道名称，如 "支付方式1", "WeChat Pay" 等 */
    name: string;
    /** 该渠道下的金额 */
    revenue: number;
    /** 该渠道金额占总金额的百分比字符串，如 "12.43%" */
    percent: string;
}

/**
 * 餐厅维度的明细数据项
 */
export interface RestaurantBreakdownItem {
    /** 餐厅唯一标识符 */
    restaurant_id: string;
    /** 餐厅名称（API 未返回时由前端根据集团餐厅列表补充） */
    restaurant_name?: string;
    /** 该餐厅本期营收 */
    revenue: number;
    /** 该餐厅上期营收 */
    revenue_last: number;
    /** 该餐厅本期订单量 */
    total_orders: number;
    /** 该餐厅上期订单量 */
    total_orders_last: number;
    /** 该餐厅平均客单价 */
    avg_order_amount: number;
    /** 该餐厅内部的支付方式占比列表 */
    payment_breakdown: PaymentDetail[];
    /** 该餐厅的趋势图数据 */
    trend: DailyTrendPoint[];
    /** 餐厅数据状态：'ok' (成功), 'db_connection_failed' (数据库连接失败) 等 */
    status: Status;
}

const enum Status {
    ok = 'ok',
    db_connection_failed = 'db_connection_failed'
}

/**
 * 趋势图时间点数据
 */
interface DailyTrendPoint {
    /** 时间坐标，通常为日期: "YYYY-MM-DD" */
    x: string;
    /** 当日对应的数值 */
    revenue: number;
}

/** 集团菜品分析 - 时间范围 */
export interface MenuSalesAnalysisTimeRange {
    start: string;
    end: string;
}

/** 集团菜品分析 - 列表行（表格） */
export interface MenuSalesAnalysisListItem {
    id: number;
    name: string;
    total_quantity: number;
    total_amount: number;
    unit_price: number;
}

/** 集团菜品分析 - 趋势点 */
export interface MenuSalesAnalysisTrendPoint {
    x: string;
    y: number;
}

/** 集团菜品分析 - 单条趋势（按 id 键） */
export interface MenuSalesAnalysisTrendItem {
    name: string;
    points: MenuSalesAnalysisTrendPoint[];
}

/** 集团菜品分析 - 饼图/环形图单项 */
export interface MenuSalesAnalysisPieItem {
    id: number;
    name: string;
    amount: number;
    ratio: number;
}

/**
 * 集团菜品分析接口返回（Tab3）
 */
export interface MenuSalesAnalysisResponse {
    group_id: string;
    mode: string;
    type: string;
    time_range: MenuSalesAnalysisTimeRange;
    restaurant_count: number;
    success_count: number;
    failed_count: number;
    revenue: number;
    list: MenuSalesAnalysisListItem[];
    /** 按 list 项 id 为 key，每条为名称与日期-数值点 */
    trend: Record<string, MenuSalesAnalysisTrendItem>;
    pie: MenuSalesAnalysisPieItem[];
    totalPieAmount: number;
    failed_restaurants: string[];
}

// ——— 集团菜单销售分析（新版 Tab：menu-catalog-options + menu-sales-by-menu-name）———

/**
 * 下拉筛选项（名称与接口 menu_name / category_name / dish_name 对齐）
 * 类别、菜品可带归属字段，供 Tab3 菜单→类别→菜品级联
 */
export interface GroupMenuCatalogOptionItem {
    id: string;
    name: string;
    /** 类别/菜品所属菜单名（与 menus[].name 一致） */
    menu_name?: string;
    /** 菜品所属类别名（与 categories[].name 一致） */
    category_name?: string;
    /** 菜品描述（接口若有，用于列表第二行） */
    item_description?: string;
    /** 菜品价格（接口若有） */
    price?: number;
    /** 菜品id（接口若有） */
    item_id?: string;
    /** 菜品类型（接口若有） */
    item_type?: string;
}

/** 集团内门店菜单合集接口规范化后的结构 */
export interface GroupMenuCatalogOptionsResponse {
    restaurants: GroupMenuCatalogOptionItem[];
    menus: GroupMenuCatalogOptionItem[];
    categories: GroupMenuCatalogOptionItem[];
    dishes: GroupMenuCatalogOptionItem[];
}

/** menu-sales-by-menu-name 时间范围（与接口 TimeRange 一致） */
export interface MenuSalesByMenuNameTimeRange {
    start: string;
    end: string;
}

/**
 * 折线图单日数据点：与接口 Point / Daily 一致；x 为日期；amount / quantity 对应「金额 / 数量」
 */
export interface MenuSalesByMenuNameCurvePoint {
    x: string;
    amount: number;
    quantity: number;
    label: string;
}

/** 与接口 Daily 同形，复用曲线点结构 */
export type MenuSalesByMenuNameDaily = MenuSalesByMenuNameCurvePoint;

/** 与接口 AverageCurve 一致 */
export interface MenuSalesByMenuNameAverageCurve {
    points: MenuSalesByMenuNameCurvePoint[];
    success_restaurant_count: number;
}

/** 与接口 Restaurant 一致：各店汇总及按日明细 */
export interface MenuSalesByMenuNameRestaurant {
    daily?: MenuSalesByMenuNameDaily[];
    restaurant_id?: string;
    restaurant_name?: string;
    total_amount?: number;
    total_quantity?: number;
}

/** 单门店菜单销售按日曲线（折线图按门店分条展示） */
export interface MenuSalesByMenuNameRestaurantCurve {
    /** 门店 id，可选；无名称时可用于前端映射昵称 */
    restaurant_id?: string;
    /** 图例与系列名 */
    restaurant_name: string;
    points: MenuSalesByMenuNameCurvePoint[];
}

/** 门店菜单营收排名行（若后端未返回 ranking 则为空数组） */
export interface MenuSalesByMenuNameRankingItem {
    menu_name: string;
    restaurant_name: string;
    amount: number;
    quantity?: number;
}

/**
 * GET menu-sales-by-menu-name 响应（与后端契约 Response 一致）。
 * `restaurant_curves` 为归一化产物：接口直出或由各店 `restaurants[].daily` 拼装，供折线图使用。
 */
export interface MenuSalesByMenuNameResponse {
    average_curve: MenuSalesByMenuNameAverageCurve;
    failed_count: number;
    failed_restaurants: string[];
    menu_name: string;
    mode: string;
    restaurant_count: number;
    restaurant_ids: string;
    restaurants: MenuSalesByMenuNameRestaurant[];
    string_id: string;
    success_count: number;
    time_range: MenuSalesByMenuNameTimeRange;
    /**
     * 各门店趋势线；优先取接口扁平字段，否则由 `restaurants[].daily` 生成
     */
    restaurant_curves: MenuSalesByMenuNameRestaurantCurve[];
    /** 右侧列表副标题 */
    subtitle?: string;
    ranking: MenuSalesByMenuNameRankingItem[];
}

/** 空响应：错误或未返回时使用 */
export function emptyMenuSalesByMenuNameResponse(): MenuSalesByMenuNameResponse {
    return {
        average_curve: { points: [], success_restaurant_count: 0 },
        failed_count: 0,
        failed_restaurants: [],
        menu_name: '',
        mode: '',
        restaurant_count: 0,
        restaurant_ids: '',
        restaurants: [],
        string_id: '',
        success_count: 0,
        time_range: { start: '', end: '' },
        restaurant_curves: [],
        ranking: [],
    };
}

function catalogItemId(r: Record<string, unknown>, nameStr: string): string {
    const id = r.id ?? r._id ?? r.string_id ?? r.restaurant_id ?? r.menu_id ?? r.dish_id ?? nameStr;
    return String(id ?? nameStr);
}

/**
 * 从菜品原始对象解析描述、价格、类型、菜品业务 ID（与根级 `dishes` 列表 `mapCatalogDishItems` 别名一致）。
 * 树形展开 `pushDishesFromArray` 也必须合并，否则搜索表里只有根级 `dishes` 带这些字段。
 */
function optionalDishMetaFromRaw(r: Record<string, unknown>): Partial<
    Pick<GroupMenuCatalogOptionItem, 'item_description' | 'price' | 'item_type' | 'item_id'>
> {
    const descRaw = r.item_description;
    const item_description =
        descRaw != null && String(descRaw).trim() !== '' ? String(descRaw).trim() : undefined;
    const priceRaw = r.price ?? r.price_1;
    let price: number | undefined;
    if (typeof priceRaw === 'number' && Number.isFinite(priceRaw)) {
        price = priceRaw;
    } else if (priceRaw != null && String(priceRaw).trim() !== '') {
        const n = Number(priceRaw);
        if (Number.isFinite(n)) price = n;
    }
    const itemIdRaw = r.item_id ?? r.menu_item_id ?? r.dish_id;
    const item_id =
        itemIdRaw != null && String(itemIdRaw).trim() !== '' ? String(itemIdRaw) : undefined;
    const typeRaw = r.item_type ?? r.dish_item_type;
    const item_type =
        typeRaw != null && String(typeRaw).trim() !== '' ? String(typeRaw) : undefined;
    return {
        ...(item_description != null ? { item_description } : {}),
        ...(price != null ? { price } : {}),
        ...(item_type != null ? { item_type } : {}),
        ...(item_id != null ? { item_id } : {}),
    };
}

/**
 * 餐厅 / 菜单等扁平列表：{ id, name }
 */
/**
 * 与 `mapCatalogOptionItems` 菜单项展示名顺序一致，供树形展开写入 `menu_name`，避免与下拉选中值不一致
 */
function catalogMenuNodeDisplayName(m: Record<string, unknown>): string | undefined {
    const raw = m.name ?? m.nickname ?? m.label ?? m.title ?? m.menu_name;
    if (raw == null || String(raw) === '') return undefined;
    return String(raw);
}

/** 子类别节点展示名：与 `mapCatalogCategoryItems` 中 name 字段解析顺序一致 */
function catalogCategoryNodeDisplayName(c: Record<string, unknown>): string | undefined {
    const raw = c.name ?? c.category_name ?? c.second_menu_name ?? c.nickname ?? c.title;
    if (raw == null || String(raw) === '') return undefined;
    return String(raw);
}

function mapCatalogOptionItems(arr: unknown): GroupMenuCatalogOptionItem[] {
    if (!Array.isArray(arr)) return [];
    const out: GroupMenuCatalogOptionItem[] = [];
    for (const item of arr) {
        if (!item || typeof item !== 'object') continue;
        const r = item as Record<string, unknown>;
        const name = r.name ?? r.nickname ?? r.label ?? r.title ?? r.menu_name;
        if (name == null || String(name) === '') continue;
        const nameStr = String(name);
        out.push({ id: catalogItemId(r, nameStr), name: nameStr });
    }
    return out;
}

/**
 * 类别列表：解析所属菜单 menu_name（级联）
 */
function mapCatalogCategoryItems(arr: unknown): GroupMenuCatalogOptionItem[] {
    if (!Array.isArray(arr)) return [];
    const out: GroupMenuCatalogOptionItem[] = [];
    for (const item of arr) {
        if (!item || typeof item !== 'object') continue;
        const r = item as Record<string, unknown>;
        const name = r.name ?? r.category_name ?? r.second_menu_name ?? r.nickname ?? r.title;
        if (name == null || String(name) === '') continue;
        const nameStr = String(name);
        const menuRaw = r.menu_name ?? r.menuName ?? r.first_menu_name ?? r.belonged_menu_name ?? r.menu;
        const menu_name = menuRaw != null && String(menuRaw) !== '' ? String(menuRaw) : undefined;
        out.push({
            id: catalogItemId(r, nameStr),
            name: nameStr,
            menu_name,
        });
    }
    return out;
}

/**
 * 从接口 `menus` 树形结构展开子类别（常见：每项含 categories / second_menus / children），并写入所属一级菜单名
 */
function flattenCategoriesUnderMenus(menusRaw: unknown): GroupMenuCatalogOptionItem[] {
    if (!Array.isArray(menusRaw)) return [];
    const out: GroupMenuCatalogOptionItem[] = [];
    for (const rawMenu of menusRaw) {
        if (!rawMenu || typeof rawMenu !== 'object') continue;
        const m = rawMenu as Record<string, unknown>;
        const parentMenuName = catalogMenuNodeDisplayName(m);
        if (!parentMenuName) continue;
        const nested =
            m.categories ??
            m.category_list ??
            m.second_menus ??
            m.children ??
            m.subcategories ??
            m.sub_categories;
        if (!Array.isArray(nested)) continue;
        for (const item of nested) {
            if (!item || typeof item !== 'object') continue;
            const r = item as Record<string, unknown>;
            const nameStr = catalogCategoryNodeDisplayName(r);
            if (!nameStr) continue;
            out.push({
                id: catalogItemId(r, `${parentMenuName}:${nameStr}`),
                name: nameStr,
                menu_name: parentMenuName,
            });
        }
    }
    return out;
}

/** 合并根级 categories 与 menus 下展开的类别，按「菜单名+类别名」去重 */
function mergeCategoryLists(
    root: GroupMenuCatalogOptionItem[],
    nested: GroupMenuCatalogOptionItem[]
): GroupMenuCatalogOptionItem[] {
    const seen = new Set<string>();
    const merged: GroupMenuCatalogOptionItem[] = [];
    for (const c of [...root, ...nested]) {
        const mk = (c.menu_name ?? '').trim();
        const key = `${mk}\0${c.name}`;
        if (seen.has(key)) continue;
        seen.add(key);
        merged.push(c);
    }
    return merged;
}

/** 类别节点下常见「菜品列表」字段（不同后端命名不一） */
function dishArrayFromCategoryNode(c: Record<string, unknown>): unknown[] | null {
    const raw =
        c.dishes ??
        c.dish_list ??
        c.items ??
        c.menu_items ??
        c.products ??
        c.goods ??
        c.foods ??
        c.dishItems;
    if (!Array.isArray(raw)) return null;
    return raw;
}

function pushDishesFromArray(
    dishArr: unknown[],
    parentMenuName: string,
    categoryName: string | undefined,
    out: GroupMenuCatalogOptionItem[]
): void {
    for (const rawDish of dishArr) {
        if (rawDish == null) continue;
        /** 真实接口常见：`dishes` 为菜名字符串数组，非对象 */
        if (typeof rawDish === 'string') {
            const nameStr = rawDish.trim();
            if (!nameStr) continue;
            out.push({
                id: catalogItemId(
                    { name: nameStr } as Record<string, unknown>,
                    `${parentMenuName}:${categoryName ?? ''}:${nameStr}`
                ),
                name: nameStr,
                menu_name: parentMenuName,
                category_name: categoryName,
            });
            continue;
        }
        if (typeof rawDish !== 'object') continue;
        const r = rawDish as Record<string, unknown>;
        const name = r.name ?? r.dish_name ?? r.item_name ?? r.title ?? r.product_name;
        if (name == null || String(name) === '') continue;
        const nameStr = String(name);
        out.push({
            id: catalogItemId(r, `${parentMenuName}:${categoryName ?? ''}:${nameStr}`),
            name: nameStr,
            menu_name: parentMenuName,
            category_name: categoryName,
            ...optionalDishMetaFromRaw(r),
        });
    }
}

/**
 * 从 `menus` → 子类别 → 菜品 树展开菜品，写入 menu_name / category_name。
 * 另支持：菜品直接挂在菜单下（无子类别）、或子类别与菜品同一数组（仅含菜名字段的项当作菜品）。
 */
function flattenDishesUnderMenus(menusRaw: unknown): GroupMenuCatalogOptionItem[] {
    if (!Array.isArray(menusRaw)) return [];
    const out: GroupMenuCatalogOptionItem[] = [];
    for (const rawMenu of menusRaw) {
        if (!rawMenu || typeof rawMenu !== 'object') continue;
        const m = rawMenu as Record<string, unknown>;
        const parentMenuName = catalogMenuNodeDisplayName(m);
        if (!parentMenuName) continue;

        /** 一级菜单下直接挂菜品（无 categories 时常见） */
        const menuDirect =
            m.dishes ?? m.dish_list ?? m.menu_items ?? m.items ?? m.products ?? m.goods ?? m.foods;
        if (Array.isArray(menuDirect)) {
            pushDishesFromArray(menuDirect, parentMenuName, undefined, out);
        }

        const nested =
            m.categories ??
            m.category_list ??
            m.second_menus ??
            m.children ??
            m.subcategories ??
            m.sub_categories;
        if (!Array.isArray(nested)) continue;
        for (const rawCat of nested) {
            if (!rawCat || typeof rawCat !== 'object') continue;
            const c = rawCat as Record<string, unknown>;
            const categoryName = catalogCategoryNodeDisplayName(c);
            const dishArr = dishArrayFromCategoryNode(c);
            if (dishArr && dishArr.length > 0) {
                pushDishesFromArray(dishArr, parentMenuName, categoryName, out);
            }
        }
    }
    return out;
}

/** 合并根级 dishes 与树形展开的菜品，按「菜单+类别+菜名」去重 */
function mergeDishLists(
    root: GroupMenuCatalogOptionItem[],
    nested: GroupMenuCatalogOptionItem[]
): GroupMenuCatalogOptionItem[] {
    const seen = new Set<string>();
    const merged: GroupMenuCatalogOptionItem[] = [];
    for (const d of [...root, ...nested]) {
        const mk = (d.menu_name ?? '').trim();
        const ck = (d.category_name ?? '').trim();
        const key = `${mk}\0${ck}\0${d.name}`;
        if (seen.has(key)) continue;
        seen.add(key);
        merged.push(d);
    }
    return merged;
}

/**
 * 菜品列表：解析所属菜单、类别（级联）
 */
function mapCatalogDishItems(arr: unknown): GroupMenuCatalogOptionItem[] {
    if (!Array.isArray(arr)) return [];
    const out: GroupMenuCatalogOptionItem[] = [];
    for (const item of arr) {
        if (item == null) continue;
        if (typeof item === 'string') {
            const nameStr = item.trim();
            if (!nameStr) continue;
            out.push({
                id: catalogItemId({ name: nameStr } as Record<string, unknown>, nameStr),
                name: nameStr,
                menu_name: undefined,
                category_name: undefined,
            });
            continue;
        }
        if (typeof item !== 'object') continue;
        const r = item as Record<string, unknown>;
        const name = r.name ?? r.dish_name ?? r.item_name ?? r.title;
        if (name == null || String(name) === '') continue;
        const nameStr = String(name);
        const menuRaw = r.menu_name ?? r.menuName ?? r.first_menu_name ?? r.belonged_menu_name ?? r.menu;
        const catRaw =
            r.category_name ?? r.categoryName ?? r.second_menu_name ?? r.subcategory_name ?? r.class_name;
        const menu_name = menuRaw != null && String(menuRaw) !== '' ? String(menuRaw) : undefined;
        const category_name = catRaw != null && String(catRaw) !== '' ? String(catRaw) : undefined;
        out.push({
            id: catalogItemId(r, nameStr),
            name: nameStr,
            menu_name,
            category_name,
            ...optionalDishMetaFromRaw(r),
        });
    }
    return out;
}

/**
 * 仅归一化接口响应中的 `menus` 字段（一级菜单列表），供状态单独持久化、后续菜单下拉使用
 */
export function normalizeMenuCatalogMenusFromApi(res: unknown): GroupMenuCatalogOptionItem[] {
    if (!res || typeof res !== 'object') return [];
    const menus = (res as Record<string, unknown>).menus;
    return mapCatalogOptionItems(menus);
}

/**
 * 归一化 menu-catalog-options 响应（兼容多种字段命名）
 */
export function normalizeGroupMenuCatalogOptions(raw: unknown): GroupMenuCatalogOptionsResponse {
    const empty: GroupMenuCatalogOptionsResponse = { restaurants: [], menus: [], categories: [], dishes: [] };
    if (!raw || typeof raw !== 'object') return empty;
    const o = raw as Record<string, unknown>;

    const rootCategories = mapCatalogCategoryItems(o.categories ?? o.category_list ?? o.second_menus);
    const nestedCategories = flattenCategoriesUnderMenus(o.menus);
    const categories = mergeCategoryLists(rootCategories, nestedCategories);

    const rootDishes = mapCatalogDishItems(o.dishes ?? o.dish_list ?? o.items);
    const nestedDishes = flattenDishesUnderMenus(o.menus);
    const dishes = mergeDishLists(rootDishes, nestedDishes);

    return {
        restaurants: mapCatalogOptionItems(o.restaurants),
        menus: mapCatalogOptionItems(o.menus),
        categories,
        dishes,
    };
}

function parseCurvePoint(p: unknown): MenuSalesByMenuNameCurvePoint | null {
    if (!p || typeof p !== 'object') return null;
    const pt = p as Record<string, unknown>;
    const x = String(pt.x ?? '');
    const amountRaw = Number(pt.amount);
    const quantityRaw = Number(pt.quantity);
    const amount = Number.isFinite(amountRaw) ? amountRaw : 0;
    const quantity = Number.isFinite(quantityRaw) ? quantityRaw : 0;
    const label = pt.label != null ? String(pt.label) : '';
    if (!x) return null;
    return { x, amount, quantity, label };
}

/** 解析单条门店曲线（对象须含 points 数组） */
function parseRestaurantCurveRow(item: unknown): MenuSalesByMenuNameRestaurantCurve | null {
    if (!item || typeof item !== 'object') return null;
    const r = item as Record<string, unknown>;
    const ptsRaw = r.points ?? r.data ?? r.values;
    if (!Array.isArray(ptsRaw)) return null;
    const points = ptsRaw.map(parseCurvePoint).filter(Boolean) as MenuSalesByMenuNameCurvePoint[];
    if (points.length === 0) return null;
    const restaurant_id =
        r.restaurant_id != null && String(r.restaurant_id) !== ''
            ? String(r.restaurant_id)
            : r.string_id != null && String(r.string_id) !== ''
                ? String(r.string_id)
                : undefined;
    const restaurant_name = String(
        r.restaurant_name ?? r.restaurantName ?? r.name ?? r.store_name ?? r.nickname ?? r.label ?? ''
    );
    if (!restaurant_name.trim() && !restaurant_id) return null;
    return {
        restaurant_id,
        restaurant_name: restaurant_name.trim() || restaurant_id || '',
        points,
    };
}

function parseRestaurantCurvesFromUnknown(raw: unknown): MenuSalesByMenuNameRestaurantCurve[] {
    if (!Array.isArray(raw)) return [];
    const out: MenuSalesByMenuNameRestaurantCurve[] = [];
    for (const item of raw) {
        const row = parseRestaurantCurveRow(item);
        if (row) out.push(row);
    }
    return out;
}

/** 解析接口 `restaurants[]`：对象（含 daily）或历史版本下的门店 id 字符串 */
function parseMenuSalesRestaurantRow(item: unknown): MenuSalesByMenuNameRestaurant | null {
    if (item == null) return null;
    if (typeof item === 'string') {
        const id = item.trim();
        return id ? { restaurant_id: id } : null;
    }
    if (typeof item !== 'object') return null;
    const r = item as Record<string, unknown>;
    const dailyRaw = r.daily;
    const daily = Array.isArray(dailyRaw)
        ? (dailyRaw.map(parseCurvePoint).filter(Boolean) as MenuSalesByMenuNameCurvePoint[])
        : undefined;
    const tid = r.restaurant_id ?? r.string_id;
    const restaurant_id = tid != null && String(tid) !== '' ? String(tid) : undefined;
    const restaurant_name =
        r.restaurant_name != null && String(r.restaurant_name) !== '' ? String(r.restaurant_name) : undefined;
    const ta = Number(r.total_amount);
    const tq = Number(r.total_quantity);
    return {
        restaurant_id,
        restaurant_name,
        total_amount: Number.isFinite(ta) ? ta : undefined,
        total_quantity: Number.isFinite(tq) ? tq : undefined,
        daily,
    };
}

function parseMenuSalesRestaurantsFromUnknown(raw: unknown): MenuSalesByMenuNameRestaurant[] {
    if (!Array.isArray(raw)) return [];
    const out: MenuSalesByMenuNameRestaurant[] = [];
    for (const item of raw) {
        const row = parseMenuSalesRestaurantRow(item);
        if (row) out.push(row);
    }
    return out;
}

/** 由各店 `daily` 构造折线图门店曲线（无接口扁平 curves 时使用） */
function restaurantCurvesFromRestaurantRows(rows: MenuSalesByMenuNameRestaurant[]): MenuSalesByMenuNameRestaurantCurve[] {
    const out: MenuSalesByMenuNameRestaurantCurve[] = [];
    for (const r of rows) {
        const points = r.daily;
        if (!points || points.length === 0) continue;
        const restaurant_id = r.restaurant_id;
        const nameRaw = (r.restaurant_name ?? '').trim();
        const restaurant_name = nameRaw || restaurant_id || '';
        if (!restaurant_name) continue;
        out.push({ restaurant_id, restaurant_name, points });
    }
    return out;
}

/**
 * 归一化 menu-sales-by-menu-name 响应（average_curve + 元数据 + 可选 ranking）
 */
export function normalizeMenuSalesByMenuNameResponse(raw: unknown): MenuSalesByMenuNameResponse {
    const base = emptyMenuSalesByMenuNameResponse();
    if (!raw || typeof raw !== 'object') return base;
    const o = raw as Record<string, unknown>;

    let points: MenuSalesByMenuNameCurvePoint[] = [];
    let success_restaurant_count = 0;
    const ac = o.average_curve;
    let restaurant_curves: MenuSalesByMenuNameRestaurantCurve[] = parseRestaurantCurvesFromUnknown(
        o.restaurant_curves ??
        o.restaurantCurves ??
        o.restaurant_series ??
        o.store_curves ??
        o.per_restaurant ??
        o.curves
    );
    if (ac && typeof ac === 'object') {
        const aco = ac as Record<string, unknown>;
        const src = Number(aco.success_restaurant_count);
        success_restaurant_count = Number.isFinite(src) ? src : 0;
        const pts = aco.points;
        if (Array.isArray(pts)) {
            points = pts.map(parseCurvePoint).filter(Boolean) as MenuSalesByMenuNameCurvePoint[];
        }
        if (restaurant_curves.length === 0) {
            const nested = aco.series ?? aco.curves ?? aco.restaurant_curves;
            restaurant_curves = parseRestaurantCurvesFromUnknown(nested);
        }
    }

    const restaurants = parseMenuSalesRestaurantsFromUnknown(o.restaurants);
    if (restaurant_curves.length === 0) {
        restaurant_curves = restaurantCurvesFromRestaurantRows(restaurants);
    }

    let time_range = base.time_range;
    const tr = o.time_range ?? o.timeRange;
    if (tr && typeof tr === 'object') {
        const tro = tr as Record<string, unknown>;
        time_range = {
            start: String(tro.start ?? ''),
            end: String(tro.end ?? ''),
        };
    }

    let ranking: MenuSalesByMenuNameRankingItem[] = [];
    const rk = o.restaurants;
    if (Array.isArray(rk)) {
        ranking = rk
            .map((item) => {
                const restaurant_name = String(item.restaurant_name ?? '');
                const amount = Number(item.total_amount ?? 0);
                const quantity = Number(item.total_quantity ?? 0);
                return { restaurant_name, amount, quantity };
            }) as MenuSalesByMenuNameRankingItem[];
    }

    const fr = o.failed_restaurants ?? o.failedRestaurants;
    const failed_restaurants = Array.isArray(fr) ? fr.map((x) => String(x)) : [];

    return {
        average_curve: { points, success_restaurant_count },
        failed_count: Number(o.failed_count) || 0,
        failed_restaurants,
        menu_name: String(o.menu_name ?? ''),
        mode: String(o.mode ?? ''),
        restaurant_count: Number(o.restaurant_count) || 0,
        restaurant_ids: String(o.restaurant_ids ?? ''),
        restaurants,
        string_id: String(o.string_id ?? ''),
        success_count: Number(o.success_count) || 0,
        time_range,
        restaurant_curves,
        subtitle: String(o.menu_name ?? ''),
        ranking,
    };
}